import { DevLogger } from '~/utils/DevLogger';

/**
 * How the portal learns that a chat ended, when Salesforce does not say so.
 *
 * `onEmbeddedMessagingSessionStatusUpdate` is the one public event that reports an end,
 * and it is not enough on its own: Agentforce ends an idle session within about a
 * minute, so a user who presses End chat after that changes no status and fires no
 * event at all. These two nets cover that gap, and both were arrived at by measuring the
 * widget rather than reading its names.
 */

interface ISaltclickEndSignalHandlers {
  /** A chat ended, and the conversation behind it can be retired. */
  onChatEnded: (trigger: string) => void;
  /**
   * A conversation is in play again, which is what makes handling another end
   * meaningful.
   *
   * Watched here as well as on `onEmbeddedMessagingConversationStarted` because that is a
   * Salesforce event, and on this deployment those are not safe to depend on — four of
   * the five documented end signals never arrive. If the one that releases the caller's
   * one-per-end guard is among them, the guard latches after the first end and no chat
   * ends again for the rest of the page load.
   */
  onConversationSeen?: (trigger: string) => void;
}

/**
 * Hosts a `postMessage` end signal may legitimately come from.
 *
 * `window` messages arrive from anywhere — other iframes, extensions, analytics — and
 * acting on one closes a conversation, so the origin is checked rather than trusted. The
 * RPC port needs no such check: a port is handed to exactly one peer.
 */
const SALESFORCE_ORIGIN_PATTERN =
  /^https:\/\/[a-z0-9.-]+\.(my\.site\.com|salesforce-scrt\.com|salesforce\.com)$/i;

/**
 * RPC methods from the widget's iframe that mean "this chat has ended".
 *
 * `cwcresetclienttoinitialstate` is the widget returning itself to its launcher state,
 * which is what End chat does to it and what nothing else does. Its own client
 * registers a handler for the name, so it is a message the host is meant to act on
 * rather than internal chatter.
 */
const END_SIGNAL_RPC_METHODS = ['cwcresetclienttoinitialstate'];

/**
 * Matches the harness, and no longer slower than it: the latency turned out to be
 * visible after all. If this is the net that catches an end, everything the user waits
 * through — the widget sitting in its ended state, the launcher inert while the
 * conversation is retired — starts from here, so a second of polling is a second of
 * apparently nothing happening.
 */
const STORAGE_POLL_MS = 1000;
const CONVERSATION_ID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/g;
/** Which web-storage keys are the widget's. Its key names vary by client version. */
const CONVERSATION_KEY_PATTERN = /embedded|esw|messaging|conversation/i;

/**
 * How long after the portal clears the session its own signals are ignored for.
 *
 * Bounded rather than a flag that waits to be consumed: a clear that turns out to have
 * had no conversation to lose would leave such a flag standing, and the next thing it
 * swallowed would be a real End chat. Five seconds is long enough for a reset to finish
 * arriving and far short of a user logging in and ending a chat.
 */
const OWN_CLEAR_QUIET_MS = 5000;

let handlers: ISaltclickEndSignalHandlers | null = null;
let quietUntil = 0;
/** Ports keep their listener for the document's life; `WeakSet` stops a second one. */
const watchedPorts = new WeakSet<MessagePort>();
let tappedMessageChannel: typeof MessageChannel | undefined;
let originalMessageChannel: typeof MessageChannel | undefined;
let storageTimer: number | undefined;
let lastConversationIds = '';

/**
 * Passes on an end, unless the portal caused it.
 *
 * Both nets watch for the widget returning to its initial state, and `clearSession` puts
 * it there — so logout, an account handover and the end flow's own clear each look
 * exactly like the user pressing End chat. Acting on those is the mistake this whole
 * ticket keeps making: a handover would read as the account that just logged IN ending a
 * chat, and close the conversation they were meant to come back to.
 */
const reportChatEnded = (trigger: string): void => {
  if (Date.now() < quietUntil) {
    DevLogger.log(
      `[Saltclick] [${trigger}] arrived just after the portal cleared the session — ` +
        'that is our own reset, not a chat ending',
    );

    return;
  }

  handlers?.onChatEnded(trigger);
};

/**
 * Ignores the end signals the portal's own `clearSession` is about to produce. Called by
 * `clearSaltclickSession`, so every path that clears is covered by one call.
 */
export const muteSaltclickEndSignals = (): void => {
  quietUntil = Date.now() + OWN_CLEAR_QUIET_MS;
};

/**
 * Every name an RPC message could be carrying its method under.
 *
 * All three, because guessing one wrong is silent. Measured on dev (2026-08-31): this
 * deployment's envelope is `{type: 'rpc-call', event: 'cwcresetclienttoinitialstate'}` —
 * the method is in `event`, and `type` holds the envelope kind. Reading `method || type`
 * therefore matched the string `rpc-call` on every message and the end signal on none, so
 * this net reported nothing for as long as it existed while looking exactly like a net
 * that was working.
 *
 * `unknown` in, because this reads messages from every channel and every frame, not only
 * the widget's.
 */
const readRpcMethods = (data: unknown): string[] => {
  if (typeof data !== 'object' || data === null) return [];

  const { method, event, type } = data as { method?: unknown; event?: unknown; type?: unknown };

  return [method, event, type].filter((name): name is string => typeof name === 'string');
};

/** The end-signal method this message carries, if any of its names is one. */
const readEndSignalMethod = (data: unknown): string | undefined =>
  readRpcMethods(data).find((name) => END_SIGNAL_RPC_METHODS.includes(name));

const watchPort = (port: MessagePort): void => {
  if (watchedPorts.has(port)) return;
  watchedPorts.add(port);

  port.addEventListener('message', (event: MessageEvent) => {
    const method = readEndSignalMethod(event.data);
    if (!method) return;

    // Method name only, never the payload: the bridge carries the identity JWT.
    reportChatEnded(`rpc:${method}`);
  });
};

/**
 * The same method, one layer up.
 *
 * Cheap insurance rather than a second net: the bootstrap may re-post what crosses the
 * port, and a signal that arrives twice costs nothing — the caller's guard sees to that.
 * A signal that arrives on the layer nobody was watching costs the whole feature.
 */
const onWindowMessage = (event: MessageEvent): void => {
  // The origin string itself, not a parsed URL: an opaque origin posts the literal
  // `"null"`, which `new URL` throws on — inside a message listener that fires for every
  // frame on the page.
  if (!SALESFORCE_ORIGIN_PATTERN.test(event.origin)) return;

  const method = readEndSignalMethod(event.data);
  if (!method) return;

  reportChatEnded(`post:${method}`);
};

/**
 * Taps the RPC bridge the widget talks to its iframe over.
 *
 * Needed because port messages are NOT delivered as `window` `'message'` events, so a
 * page listening on `window` sees nothing and concludes nothing happened — which is
 * exactly the wrong conclusion, and the reason End chat looked undetectable from here
 * for as long as it did.
 *
 * Patching the constructor is what makes this possible: the bootstrap builds the channel
 * in this document, so wrapping `MessageChannel` before its script loads hands us both
 * ports. It has to be a subclass rather than a wrapper function so that everything else
 * about `MessageChannel` — `instanceof`, port transfer — stays true of it.
 *
 * Scoped as tightly as the widget allows: React's scheduler builds its own channel at
 * import time, long before this runs, so the hot path of React scheduling is never
 * touched; and the patch comes off again on teardown. Ports found while it was on keep
 * their listener, which is what lets a remount pick the same widget back up.
 */
const installRpcTap = (): void => {
  if (tappedMessageChannel && window.MessageChannel === tappedMessageChannel) return;

  originalMessageChannel = window.MessageChannel;
  if (!originalMessageChannel) return;

  tappedMessageChannel = class TappedMessageChannel extends originalMessageChannel {
    constructor() {
      super();
      watchPort(this.port1);
      watchPort(this.port2);
    }
  };
  window.MessageChannel = tappedMessageChannel;
};

/**
 * Every conversation id the widget currently has on this origin, as one comparable
 * string.
 *
 * Both storages, because which one it uses depends on the deployment's session-continuity
 * setting, and a value under a key we did not expect reads as "no conversation" — the
 * answer that quietly does nothing.
 */
const readStoredConversationIds = (): string => {
  const ids = new Set<string>();

  [window.sessionStorage, window.localStorage].forEach((store) => {
    for (let index = 0; index < store.length; index += 1) {
      const key = store.key(index);
      if (key && CONVERSATION_KEY_PATTERN.test(key)) {
        const found = (store.getItem(key) ?? '').match(CONVERSATION_ID_PATTERN);
        found?.forEach((id) => ids.add(id));
      }
    }
  });

  return [...ids].sort().join(',');
};

/**
 * The same ids as a list, for a caller that has to ask Salesforce about them.
 *
 * Exported rather than derived again by the caller: which storage keys are the widget's
 * and what a conversation id looks like are this module's answers, and a second
 * implementation of them would drift from the watcher that actually reports ends.
 */
export const getStoredSaltclickConversationIds = (): string[] => {
  const ids = readStoredConversationIds();

  return ids ? ids.split(',') : [];
};

/**
 * Makes the widget forget the conversation on THIS ORIGIN, which `clearSession` does not.
 *
 * Measured on dev (2026-08-31), and the reason a retired chat could not be followed by a
 * new one: the widget decides what to resume from what it kept in storage here, and it
 * looks before anything on the page can intervene. An id left behind after the server-side
 * close sends it to resume a conversation that no longer exists — Salesforce refuses, the
 * widget minimizes itself and sends `cwcresetclienttoinitialstate`, and the user watches
 * the chat open, sit on its hello screen and shut itself. Clicking again does it again,
 * because the id is still there. Only a new document was getting out of it.
 *
 * Safe only where it is called: after every conversation the account had open has just
 * been deleted, so an id still in storage is dead by definition and there is nothing to
 * preserve. It is not a general-purpose "reset the widget" — pointed at a live
 * conversation this would take it away.
 *
 * Keys are matched, not assumed, and a matching key is only touched when its value
 * carries a conversation id: deployment settings live under matching names too, and
 * dropping those costs the next `init` its configuration.
 */
export const purgeStoredSaltclickConversation = (): string[] => {
  const removed: string[] = [];

  [window.sessionStorage, window.localStorage].forEach((store) => {
    const doomed: string[] = [];

    for (let index = 0; index < store.length; index += 1) {
      const key = store.key(index);
      const value = key ? (store.getItem(key) ?? '') : '';
      // Collected first, deleted after: removing during the walk reindexes the store and
      // skips the next key.
      // `match`, not `test`: `CONVERSATION_ID_PATTERN` is global, and `test` on a global
      // regex advances its `lastIndex` — so alternating keys would get alternating answers.
      if (key && CONVERSATION_KEY_PATTERN.test(key) && value.match(CONVERSATION_ID_PATTERN)) {
        doomed.push(key);
      }
    }

    doomed.forEach((key) => {
      try {
        store.removeItem(key);
        removed.push(key);
      } catch (err) {
        DevLogger.error(`[Saltclick] Could not forget the stored conversation in ${key}: `, err);
      }
    });
  });

  DevLogger.log(
    removed.length
      ? `[Saltclick] Forgot ${removed.length} stored conversation key(s) so the widget does ` +
          `not resume a closed conversation: ${removed.join(', ')}`
      : '[Saltclick] No stored conversation state to forget',
  );

  // The watcher must not read this as the conversation ending: it is the same "id was
  // there, now it is gone" transition, for a conversation that was closed a moment ago.
  lastConversationIds = readStoredConversationIds();

  return removed;
};

/**
 * Watches the conversation state the widget keeps in web storage on this origin.
 *
 * The net that depends on Salesforce telling us nothing: whatever the widget does on End
 * chat, it has to record here, because this is where it looks on the next load to decide
 * whether to resume. So a conversation id that was in storage and is now gone IS the
 * end, observed rather than announced.
 *
 * Polled rather than evented, because the `storage` event fires only for other tabs and
 * never for writes made in this document — it would never see the widget's own change.
 */
const installStorageWatcher = (): void => {
  if (storageTimer !== undefined) return;

  lastConversationIds = readStoredConversationIds();
  storageTimer = window.setInterval(() => {
    const ids = readStoredConversationIds();
    if (ids === lastConversationIds) return;

    const had = lastConversationIds;
    lastConversationIds = ids;

    // Every transition, whether or not it means anything to the nets below.
    //
    // This is the cheapest true answer to "how many conversations does this widget have",
    // and the only one that costs nothing: `conversation/list` is three round trips, while
    // the widget writes its own answer here. Two ids at once is a widget in two
    // conversations, which the portal never asks for and cannot cause — it starts none —
    // but which it CAN hide, since the widget relaunches a window that was open when
    // `clearSession` reset it, and a relaunched window starts a conversation. That is
    // exactly the sort of thing that is unfalsifiable from a screenshot and obvious from
    // one log line.
    DevLogger.log(`[Saltclick] Widget conversation ids: ${had || '(none)'} → ${ids || '(none)'}`);

    if (!ids) {
      // Gone is the end. A swap is the opposite of one — a new conversation started.
      if (had) reportChatEnded('storage: the widget forgot its conversation');

      return;
    }

    // Appeared, or replaced: either way a conversation is in play, which is the news the
    // caller's guard needs and cannot reliably get from Salesforce.
    handlers?.onConversationSeen?.('storage: a conversation id appeared');
  }, STORAGE_POLL_MS);
};

/**
 * Starts watching for a chat ending, and returns the teardown.
 *
 * Idempotent, and safe to call again on a remount: the widget is init'd once per
 * document and outlives the React tree that booted it, so a second install re-attaches
 * to the same widget rather than building a second set of nets.
 *
 * Call it before the bootstrap script is appended — the RPC tap has to be in place
 * before the widget builds its channel, and there is no second chance at it.
 */
export const installSaltclickEndSignals = (
  endSignalHandlers: ISaltclickEndSignalHandlers,
): (() => void) => {
  handlers = endSignalHandlers;
  installRpcTap();
  installStorageWatcher();
  window.addEventListener('message', onWindowMessage);

  return () => {
    handlers = null;
    quietUntil = 0;
    window.removeEventListener('message', onWindowMessage);
    window.clearInterval(storageTimer);
    storageTimer = undefined;
    // Only if it is still ours. Anything else patched `MessageChannel` after we did, and
    // putting the original back would take their tap off with it.
    if (originalMessageChannel && window.MessageChannel === tappedMessageChannel) {
      window.MessageChannel = originalMessageChannel;
      tappedMessageChannel = undefined;
    }
  };
};
